import PermissionButton from "../../components/ui/PermissionButton";
import TableActions from '../../components/ui/TableActions';
import { MENU_UNITS } from "../../utils/units";
import { useEffect, useState } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";

const API = "/api";
const PAGE_SIZE = 10;

export default function MenuList() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);

  const [search, setSearch] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [uom, setUom] = useState("");

  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    total_pages: 1,
  });

  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const fetchMenuItems = async (requestedPage = page) => {
    try {
      const query = new URLSearchParams({
        page: String(requestedPage),
        page_size: String(PAGE_SIZE),
      });

      if (search.trim()) query.set("search", search.trim());
      if (departmentId) query.set("department_id", departmentId);
      if (categoryId) query.set("category_id", categoryId);
      if (uom) query.set("uom", uom);

      if (user?.role === "super_admin" && branchId) {
        query.set("branch_id", branchId);
      }

      const response = await fetch(`${API}/menu/?${query.toString()}`, {
        credentials: "include",
      });

      const data = await response.json();

      if (data.status === "success") {
        setItems(data.data || []);
        setPagination(data.pagination || {});
      } else {
        alert(data.message || "Could not load menu items.");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchFilters = async () => {
    try {
      const [departmentResponse, categoryResponse, branchResponse] =
        await Promise.all([
          fetch(`${API}/menumeta/departments`, {
            credentials: "include",
          }),
          fetch(`${API}/menumeta/categories`, {
            credentials: "include",
          }),
          fetch(`${API}/branches`, {
            credentials: "include",
          }),
        ]);

      const departmentData = await departmentResponse.json();
      const categoryData = await categoryResponse.json();
      const branchData = await branchResponse.json();

      if (departmentData.status === "success") {
        setDepartments(departmentData.data || []);
      }

      if (categoryData.status === "success") {
        setCategories(categoryData.data || []);
      }

      if (branchData.status === "success") {
        setBranches(branchData.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchMenuItems(1);
    }, 250);

    return () => clearTimeout(timer);
  }, [search, departmentId, categoryId, branchId, uom]);

  useEffect(() => {
    fetchMenuItems(page);
  }, [page]);

  const clearFilters = () => {
    setSearch("");
    setDepartmentId("");
    setCategoryId("");
    setBranchId("");
    setUom("");
    setPage(1);
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this menu item?")) return;

    try {
      const response = await fetch(`${API}/menu/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await response.json();

      if (data.status === "success") {
        await fetchMenuItems(page);
      } else {
        alert(data.message || "Could not delete menu item.");
      }
    } catch (error) {
      console.error(error);
      alert("Could not delete menu item.");
    }
  };

  const downloadTemplate = () => {
    window.open(`${API}/menu/import-template`, "_blank", "noopener,noreferrer");
  };

  const importMenu = async () => {
    if (!importFile) {
      alert("Choose an Excel file first.");
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append("file", importFile);

      const response = await fetch(`${API}/menu/import-excel`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const data = await response.json();
      setImportResult(data);

      if (data.status === "success") {
        setPage(1);
        await fetchMenuItems(1);
      }
    } catch (error) {
      console.error(error);

      setImportResult({
        status: "error",
        message: "Could not upload the Excel file.",
      });
    } finally {
      setImporting(false);
    }
  };

  const totalPages = pagination.total_pages || 1;
  const totalItems = pagination.total || 0;
  const currentPage = pagination.page || page;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div>
            <h1 className="text-2xl font-serif">Menu Items</h1>
            <p className="mt-1 text-sm text-gray-400">
              Manage menu, branch pricing, and menu imports.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <PermissionButton permissionModule="menu"
              onClick={() => {
                setImportFile(null);
                setImportResult(null);
                setShowImportModal(true);
              }}
              className="rounded-xl border border-amber-400/50 px-5 py-2 font-medium text-amber-300 hover:bg-amber-400/10"
            >
              Import Excel
            </PermissionButton>

            <PermissionButton permissionModule="menu"
              onClick={() => navigate("/admindashboard/menu-master/add")}
              className="rounded-xl bg-amber-400 px-5 py-2 font-medium text-black"
            >
              + Add Item
            </PermissionButton>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 md:grid-cols-5">
          <input
            placeholder="Search name or item code..."
            className="rounded-lg border border-white/10 bg-black/40 px-4 py-2 md:col-span-2"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />

          <select
            value={departmentId}
            onChange={(event) => setDepartmentId(event.target.value)}
            className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
          >
            <option value="">All Departments</option>

            {departments.map((department) => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
          </select>

          <select
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
            className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
          >
            <option value="">All Categories</option>

            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>

          <select
            value={uom}
            onChange={(event) => setUom(event.target.value)}
            className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
          >
            <option value="">All UOM</option>
            {MENU_UNITS.map((unit) => <option key={unit} value={unit}>{unit}</option>)}
          </select>

          {user?.role === "super_admin" && (
            <select
              value={branchId}
              onChange={(event) => setBranchId(event.target.value)}
              className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
            >
              <option value="">All Branches</option>

              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          )}

          <PermissionButton permissionModule="menu"
            onClick={clearFilters}
            className="rounded-lg border border-white/20 px-4 py-2 text-sm text-gray-300 hover:bg-white/5"
          >
            Clear Filters
          </PermissionButton>
        </div>

        <div className="overflow-hidden rounded-xl border border-white/10 bg-[#0b0b0f]">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/40 text-amber-400">
                <tr>
                  <th className="p-3 text-left">Item Code</th>
                  <th className="p-3 text-left">Item</th>
                  <th className="p-3 text-left">Category</th>
                  <th className="p-3 text-left">Department</th>
                  <th className="p-3 text-center">UOM</th>
                  <th className="p-3 text-left">Rates</th>
                  <th className="p-3 text-center">Actions</th>
                </tr>
              </thead>

              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="p-8 text-center text-gray-500">
                      No menu items found.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr
                      key={item.id}
                      className="border-t border-white/10 hover:bg-white/5"
                    >
                      <td className="p-3 text-gray-400">
                        {item.item_code || "-"}
                      </td>

                      <td className="p-3 font-medium">{item.name}</td>

                      <td className="p-3">{item.category || "-"}</td>

                      <td className="p-3">{item.department || "-"}</td>

                      <td className="p-3 text-center">{item.uom || "-"}</td>

                      <td className="p-3">
                        <div className="space-y-1">
                          {item.rates?.length ? (
                            item.rates.map((rate) => (
                              <div key={rate.branch_id}>
                                {rate.branch_name}: ₹{rate.price}
                              </div>
                            ))
                          ) : (
                            <span className="text-gray-500">No rate</span>
                          )}
                        </div>
                      </td>

                      <td className="p-3"><TableActions>
                        <div className="flex justify-center gap-3">
                          <PermissionButton permissionModule="menu" permissionAction="edit"
                            onClick={() =>
                              navigate(
                                `/admindashboard/menu-master/edit/${item.id}`,
                              )
                            }
                            className="text-amber-400 hover:underline"
                          >
                            Edit
                          </PermissionButton>

                          <PermissionButton permissionModule="menu" permissionAction="delete"
                            onClick={() => deleteItem(item.id)}
                            className="text-red-400 hover:underline"
                          >
                            Delete
                          </PermissionButton>
                        </div>
                      </TableActions></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-3 border-t border-white/10 bg-black/20 px-4 py-3 text-sm md:flex-row md:items-center md:justify-between">
            <p className="text-gray-400">
              Showing {totalItems === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1}
              {" - "}
              {Math.min(currentPage * PAGE_SIZE, totalItems)} of {totalItems}{" "}
              menu items
            </p>

            <div className="flex items-center gap-2">
              <PermissionButton permissionModule="menu"
                onClick={() => setPage((previous) => Math.max(1, previous - 1))}
                disabled={currentPage === 1}
                className="rounded-lg border border-white/15 px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Previous
              </PermissionButton>

              <span className="px-2 text-gray-300">
                Page {currentPage} of {totalPages}
              </span>

              <PermissionButton permissionModule="menu"
                onClick={() =>
                  setPage((previous) => Math.min(totalPages, previous + 1))
                }
                disabled={currentPage === totalPages}
                className="rounded-lg border border-white/15 px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </PermissionButton>
            </div>
          </div>
        </div>

        {showImportModal && (
          <MenuImportModal
            importFile={importFile}
            importing={importing}
            importResult={importResult}
            onFileChange={(file) => {
              setImportFile(file);
              setImportResult(null);
            }}
            onDownloadTemplate={downloadTemplate}
            onImport={importMenu}
            onClose={() => setShowImportModal(false)}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

function MenuImportModal({
  importFile,
  importing,
  importResult,
  onFileChange,
  onDownloadTemplate,
  onImport,
  onClose,
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0b0b0f] p-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-serif">Import Menu from Excel</h2>
            <p className="mt-2 text-sm text-gray-400">
              Repeat the same Item Code on separate rows to add rates for
              multiple branches.
            </p>
          </div>

          <PermissionButton permissionModule="menu"
            onClick={onClose}
            className="text-xl text-gray-400 hover:text-white"
          >
            ×
          </PermissionButton>
        </div>

        <PermissionButton permissionModule="menu"
          onClick={onDownloadTemplate}
          className="mt-5 rounded-xl border border-amber-400/50 px-4 py-2 text-sm text-amber-300 hover:bg-amber-400/10"
        >
          Download Excel Template
        </PermissionButton>

        <input
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(event) => onFileChange(event.target.files?.[0] || null)}
          className="mt-5 block w-full rounded-lg border border-white/10 bg-black/40 p-3 text-sm"
        />

        {importFile && (
          <p className="mt-3 text-sm text-gray-300">
            Selected: {importFile.name}
          </p>
        )}

        {importResult && (
          <div
            className={`mt-5 rounded-xl border p-4 text-sm ${
              importResult.status === "success"
                ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                : "border-red-500/30 bg-red-500/10 text-red-200"
            }`}
          >
            <p className="font-medium">{importResult.message}</p>

            {importResult.status === "success" && (
              <p className="mt-2">
                Created: {importResult.created || 0} · Skipped:{" "}
                {importResult.skipped || 0}
              </p>
            )}

            {importResult.errors?.map((error, index) => (
              <p key={index} className="mt-2">
                Row {error.row}: {error.message}
              </p>
            ))}

            {importResult.skipped_items?.map((item, index) => (
              <p key={index} className="mt-2">
                {item.name}: {item.message}
              </p>
            ))}
          </div>
        )}

        <div className="mt-8 flex justify-end gap-3">
          <PermissionButton permissionModule="menu"
            onClick={onClose}
            disabled={importing}
            className="rounded-xl border border-white/20 px-5 py-2 disabled:opacity-50"
          >
            Close
          </PermissionButton>

          <PermissionButton permissionModule="menu"
            onClick={onImport}
            disabled={!importFile || importing}
            className="rounded-xl bg-amber-400 px-5 py-2 font-medium text-black disabled:cursor-not-allowed disabled:opacity-50"
          >
            {importing ? "Importing..." : "Import Menu"}
          </PermissionButton>
        </div>
      </div>
    </div>
  );
}
